import { z } from 'zod';

// Contrato de fio da Régua de comunicação (U27), lido do legado (docs/discovery/modulos/communication-setting.md §6):
// `services/walletManagement/communicationSetting/communicationSetting.types.ts` + o que a tela lê.

const flag = z.boolean().nullish();

/** `ICommunicationSettingFull` (`communicationSetting.types.ts:1-20`), com o que a BillingAPI manda a mais. */
export const communicationSettingSchema = z.looseObject({
  id: z.string(),
  walletCode: z.number(),
  walletNumber: z.number().nullish(),
  name: z.string(),
  daysToSendBillingBeforeDueDate: z.number().nullish(),
  notifyByEmail: flag,
  notifyBillingUpdate: flag,
  notifyBillingAfterSettlement: flag,
  notifyBillingAfterOverDueDate: flag,
  resendBillingAfterOverDueDate: flag,
  /** Só leitura (`communicationSetting.types.ts:18`). */
  submitToAuto: flag,
  tenant: z.string().nullish(),
});
export type CommunicationSetting = z.infer<typeof communicationSettingSchema>;

/** `GetListApiResponseSuccess<T>` da BillingAPI. */
export const communicationSettingPageSchema = z.looseObject({
  data: z.array(communicationSettingSchema),
  page: z.number(),
  totalItems: z.number(),
  totalPages: z.number(),
  paginationToken: z.string().nullish(),
});
export type CommunicationSettingPage = z.infer<typeof communicationSettingPageSchema>;

/** Filtros do fio (`ICommunicationSettingFilters`, `communicationSetting.types.ts:23-29`) como a URL os manda. */
export interface CommunicationSettingListParams {
  readonly page: number;
  readonly size: number;
  /** Os filtros na ordem em que a pessoa aplicou (`filterContext` do legado). */
  readonly filters?: Readonly<Record<string, unknown>>;
}

/**
 * Query de `GET /CommunicationSetting`: `{ page, size, ...resto }` (`index.ts:6-14`), onde o resto são
 * os filtros do `filterValues` (`communicationSettingContext.tsx:40-46`). Sem `tenant`.
 */
export function communicationSettingListQueryParams(params: CommunicationSettingListParams): Record<string, unknown> {
  return { page: params.page, size: params.size, ...params.filters };
}

/**
 * Corpo do `POST /CommunicationSetting` (`communicationSettingContext.tsx:190-200`), na ordem do legado.
 * `walletCode` sai como **texto**: o yup `string()` converte o número do autocomplete e o
 * `yupResolver` devolve o valor convertido (`@hookform/resolvers@2.9.11`, `yup.ts:60-62`).
 * `walletNumber` é o texto digitado (ou `null`).
 */
export interface CommunicationSettingCreateBody {
  readonly daysToSendBillingBeforeDueDate: number;
  readonly name: string;
  readonly notifyBillingAfterOverDueDate: boolean | null;
  readonly notifyBillingAfterSettlement: boolean | null;
  readonly notifyBillingUpdate: boolean | null;
  readonly notifyByEmail: boolean | null;
  readonly resendBillingAfterOverDueDate: boolean | null;
  readonly walletCode: string;
  readonly walletNumber: string | null;
}

/** Corpo do `PATCH /CommunicationSetting/{id}` (`communicationSettingContext.tsx:152-162`): sem convênio nem número da carteira. */
export interface CommunicationSettingUpdateBody {
  readonly daysToSendBillingBeforeDueDate: number;
  readonly name: string;
  readonly notifyBillingAfterOverDueDate: boolean | null;
  readonly notifyBillingAfterSettlement: boolean | null;
  readonly notifyBillingUpdate: boolean | null;
  readonly notifyByEmail: boolean | null;
  readonly resendBillingAfterOverDueDate: boolean | null;
}

/** Chaves do corpo, na ordem do legado (o teste de contrato confere). */
export const CREATE_BODY_KEYS = [
  'daysToSendBillingBeforeDueDate',
  'name',
  'notifyBillingAfterOverDueDate',
  'notifyBillingAfterSettlement',
  'notifyBillingUpdate',
  'notifyByEmail',
  'resendBillingAfterOverDueDate',
  'walletCode',
  'walletNumber',
] as const;
export const UPDATE_BODY_KEYS = CREATE_BODY_KEYS.slice(0, 7);

/** Opção do autocomplete de régua que outros módulos usam: o legado lê `id`, `walletCode` e `name`. */
export const communicationSettingOptionSchema = z.looseObject({ id: z.string(), walletCode: z.union([z.number(), z.string()]).nullish(), name: z.string() });
export const communicationSettingOptionPageSchema = z.looseObject({
  data: z.array(communicationSettingOptionSchema),
  page: z.number(),
  totalItems: z.number(),
  totalPages: z.number(),
});
export type CommunicationSettingOption = z.infer<typeof communicationSettingOptionSchema>;
